import type { CbiFrontendServiceContext } from "../../frontend/context/CbiFrontendServiceContext";
import type { BalanceTransactionStaff } from "./BalanceTransactionStaff";
import type { CashTransactionStaff } from "./CashTransactionStaff";
import type { OrderPaymentDTOBase } from "./OrderPaymentDTOBase";
import type { OrderStaff } from "./OrderStaff";
import type { PaymentAttempt } from "./PaymentAttempt";
import type { PaymentCustomerSession } from "./PaymentCustomerSession";
import type { PaymentRefundStaff } from "./PaymentRefundStaff";
import type { PaymentStatus } from "./PaymentStatus";
import type { PaymentType } from "./PaymentType";
import type { StripeTransactionStaff } from "./StripeTransactionStaff";
import type { StripeTransactionType } from "./StripeTransactionType";
import type { TrackedModernBase } from "./TrackedModernBase";
import type { TransactionOtherStaff } from "./TransactionOtherStaff";

export class OrderPaymentStaff implements OrderPaymentDTOBase, TrackedModernBase {
  constructor(public readonly assignId: number, public readonly orderId: OrderStaff | null, public readonly stripeTransaction: StripeTransactionStaff | null, public readonly paymentType: PaymentType, public readonly paymentStatus: PaymentStatus, public readonly paymentServiceChangeCents: number | null, public readonly paymentPaidCents: number | null, public readonly paymentTotalCents: number | null, public readonly paymentRefundedCents: number | null, public readonly paymentToRefundCents: number | null, public readonly paymentDisputedCents: number | null, public readonly paymentDueDate: Date | null, public readonly paymentPaidDate: Date | null, public readonly paymentCapturableCents: number | null, public readonly paymentCapturableDate: Date | null, public readonly paymentRecordId: string | null, public readonly expiresOn: Date | null, public readonly paymentSource: number | null, public readonly squarePaymentId: string | null, public readonly createdOn: Date | null, public readonly createdBy: string | null, public readonly updatedOn: Date | null, public readonly updatedBy: string | null, public readonly refunds: Array<PaymentRefundStaff> | null, public readonly cashTransaction: CashTransactionStaff | null, public readonly balanceTransaction: BalanceTransactionStaff | null, public readonly transactionOther: TransactionOtherStaff | null, public readonly orderAssignId: number | null, public readonly stripePaymentIntent: string | null, public readonly stripeTransactionType: StripeTransactionType | null) {
    this.cbiServiceContext = null;
    this.orderIdLoaded = this.orderId != null;
    this.orderIdCached = this.orderId;
    this.stripeTransactionLoaded = this.stripeTransaction != null;
    this.stripeTransactionCached = this.stripeTransaction;
    this.refundsLoaded = this.refunds != null;
    this.refundsCached = this.refunds;
    this.cashTransactionLoaded = this.cashTransaction != null;
    this.cashTransactionCached = this.cashTransaction;
    this.balanceTransactionLoaded = this.balanceTransaction != null;
    this.balanceTransactionCached = this.balanceTransaction;
    this.transactionOtherLoaded = this.transactionOther != null;
    this.transactionOtherCached = this.transactionOther;
  }
  cbiServiceContext: CbiFrontendServiceContext | null;
  private orderIdLoaded: boolean;
  private orderIdCached: OrderStaff | null;
  private stripeTransactionLoaded: boolean;
  private stripeTransactionCached: StripeTransactionStaff | null;
  private refundsLoaded: boolean;
  private refundsCached: Array<PaymentRefundStaff> | null;
  private cashTransactionLoaded: boolean;
  private cashTransactionCached: CashTransactionStaff | null;
  private balanceTransactionLoaded: boolean;
  private balanceTransactionCached: BalanceTransactionStaff | null;
  private transactionOtherLoaded: boolean;
  private transactionOtherCached: TransactionOtherStaff | null;
  async orderIdLazy(): Promise<OrderStaff> {
    if (!this.orderIdLoaded) {
      this.orderIdCached = await this.cbiServiceContext!.paymentService().getOrderPaymentStaffOrderId(this.assignId);
      this.orderIdLoaded = true;
    }
    return this.orderIdCached!;
  }
  async stripeTransactionLazy(): Promise<StripeTransactionStaff | null> {
    if (!this.stripeTransactionLoaded) {
      this.stripeTransactionCached = await this.cbiServiceContext!.paymentService().getOrderPaymentStaffStripeTransaction(this.assignId);
      this.stripeTransactionLoaded = true;
    }
    return this.stripeTransactionCached;
  }
  async refundsLazy(): Promise<Array<PaymentRefundStaff>> {
    if (!this.refundsLoaded) {
      this.refundsCached = await this.cbiServiceContext!.paymentService().getOrderPaymentStaffRefunds(this.assignId);
      this.refundsLoaded = true;
    }
    return this.refundsCached!;
  }
  async cashTransactionLazy(): Promise<CashTransactionStaff | null> {
    if (!this.cashTransactionLoaded) {
      this.cashTransactionCached = await this.cbiServiceContext!.paymentService().getOrderPaymentStaffCashTransaction(this.assignId);
      this.cashTransactionLoaded = true;
    }
    return this.cashTransactionCached;
  }
  async balanceTransactionLazy(): Promise<BalanceTransactionStaff | null> {
    if (!this.balanceTransactionLoaded) {
      this.balanceTransactionCached = await this.cbiServiceContext!.paymentService().getOrderPaymentStaffBalanceTransaction(this.assignId);
      this.balanceTransactionLoaded = true;
    }
    return this.balanceTransactionCached;
  }
  async transactionOtherLazy(): Promise<TransactionOtherStaff | null> {
    if (!this.transactionOtherLoaded) {
      this.transactionOtherCached = await this.cbiServiceContext!.paymentService().getOrderPaymentStaffTransactionOther(this.assignId);
      this.transactionOtherLoaded = true;
    }
    return this.transactionOtherCached;
  }
  async cancelPayment(): Promise<void> {
    return await this.cbiServiceContext!.paymentService().cancelPaymentStaff({ assignId: this.assignId });
  }
  async paymentAttempt(): Promise<PaymentAttempt> {
    return await this.cbiServiceContext!.paymentService().paymentAttemptStaff({ assignId: this.assignId });
  }
  async paymentCustomerSession(): Promise<PaymentCustomerSession> {
    return await this.cbiServiceContext!.paymentService().paymentCustomerSessionStaff({ assignId: this.assignId });
  }
  async preparePayment(): Promise<PaymentAttempt> {
    return await this.cbiServiceContext!.paymentService().preparePaymentStaff({ assignId: this.assignId });
  }
  async sendPaymentToReader(readerId: string): Promise<void> {
    return await this.cbiServiceContext!.paymentService().sendPaymentToReaderStaff({ payment: { assignId: this.assignId }, readerId: readerId });
  }
}
